import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ClientService } from 'src/app/private/services/client.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-whatsapp-config',
  templateUrl: './whatsapp-config.component.html',
  styleUrls: ['./whatsapp-config.component.scss']
})
export class WhatsappConfigComponent implements OnInit {

  whatsappForm!: FormGroup;
  btnLoading = false;
  isPageLoading = true;

  selectedProvider = 'whatsapp';
  selected_id: any;

  passwordVisible: Record<string, boolean> = {};

  providers = [
    { key: 'whatsapp', label: 'WhatsApp Business API', icon: 'pi pi-whatsapp' }
  ];

  WHATSAPP_FIELDS = [
    { key: 'email', type: 'email', label:'email' },
    { key: 'password', type: 'password', secure: true ,label:'Password'  },
    { key: 'app_id', type: 'text' , label:'App Id' },
    { key: 'source', type: 'text' , label:'Business Mobile' },
    { key: 'api_key', type: 'password', secure: true,label:'API Key' },
    { key: 'app_name', type: 'text' ,label:'APP Name' },
    { key: 'app_token', type: 'password', secure: true ,label:'APP Token'  }
  ];

  constructor(
    private fb: FormBuilder,
    private localStorage: LocalStorageService,
    private clientService: ClientService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadFormFromApi();
  }

   beforeProviderChange(provider: string) {
  if (provider === this.selectedProvider) {
    return;
  }

  const confirmed = confirm(
    'Changing the provider will reset the current configuration. Do you want to continue?'
  );

  if (!confirmed) {
    return;
  }

  this.selectedProvider = provider;

  // Activate selected provider, deactivate others
  this.whatsAppConfigArray.controls.forEach(ctrl => {
    ctrl.patchValue({
      is_active: ctrl.get('provider')?.value === provider
    });
  });
}
  /* ---------- BUILD FORM ---------- */

  buildForm() {
    this.whatsappForm = this.fb.group({
      whats_app_config: this.fb.array([])
    });

    // Default provider
    this.whatsAppConfigArray.push(
      this.createProviderGroup('whatsapp')
    );
  }

  createProviderGroup(provider: string): FormGroup {
    const settings: any = {};

    this.WHATSAPP_FIELDS.forEach(field => {
      settings[field.key] = [''];
      if (field.secure) this.passwordVisible[field.key] = false;
    });

    return this.fb.group({
      provider: [provider],
      is_active: [true],
      settings: this.fb.group(settings)
    });
  }

  /* ---------- GETTERS ---------- */

  get whatsAppConfigArray(): FormArray {
    return this.whatsappForm.get('whats_app_config') as FormArray;
  }

  get providerForm(): FormGroup {
    return this.whatsAppConfigArray.at(0) as FormGroup;
  }

  get settingsForm(): FormGroup {
    return this.providerForm.get('settings') as FormGroup;
  }

  /* ---------- PASSWORD TOGGLE ---------- */

  togglePassword(key: string) {
    this.passwordVisible[key] = !this.passwordVisible[key];
  }

  /* ---------- SAVE ---------- */

  save() {
    const payload = {
      hospital_id: this.localStorage.getHospitalId(),
      // whats_app_config: this.whatsappForm.value.whats_app_config,
      // if array then pass [0] else pass as object
      whats_app_config: Array.isArray(this.whatsappForm.value.whats_app_config) ? this.whatsappForm.value.whats_app_config[0]?.settings : this.whatsappForm.value.whats_app_config,
      id: this.selected_id
    };

    this.btnLoading = true;

    // Check if configuration exists (selected_id is set)
    if (this.selected_id) {
      // Update existing configuration
      this.clientService.updateClientConfig(payload).subscribe({
        next: (res: any) => {
          this.toastr.success(
            'WhatsApp configuration updated successfully',
            'Success'
          );
          this.btnLoading = false;
          // Update selected_id in case it changed
          if (res && res.id) {
            this.selected_id = res.id;
          }
        },
        error: (err) => {
          console.error(err);
          this.toastr.error(
            'Failed to update WhatsApp configuration',
            'Error'
          );
          this.btnLoading = false;
        }
      });
    } else {
      // Create new configuration
      this.clientService.createClientConfig(payload).subscribe({
        next: (res: any) => {
          this.toastr.success(
            'WhatsApp configuration created successfully',
            'Success'
          );
          this.btnLoading = false;
          // Store the new ID for future updates
          if (res && res.id) {
            this.selected_id = res.id;
          }
        },
        error: (err) => {
          console.error(err);
          this.toastr.error(
            'Failed to create WhatsApp configuration',
            'Error'
          );
          this.btnLoading = false;
        }
      });
    }
  }

  /* ---------- LOAD FROM API ---------- */

  /* ---------- LOAD FROM API ---------- */

  loadFormFromApi() {
    const payload = {
      where: { hospital_id: this.localStorage.getHospitalId() },
      order: { col: '', dir: '' }
    };

    this.clientService.getAllClientConfig(payload).subscribe({
      next: (res: any) => {
        const config = this.extractWhatsAppConfig(res);

        if (config) {
          this.whatsAppConfigArray.clear();
          this.whatsAppConfigArray.push(
            this.createProviderGroup('whatsapp')
          );
          
          // Transform API response to match form structure
          const formData = {
            provider: config.provider || 'whatsapp',
            is_active: config.is_active !== undefined ? config.is_active : true,
            settings: {
              app_id: config.app_id || '',
              source: config.source || '',
              api_key: config.api_key || '',
              app_name: config.app_name || '',
              app_token: config.app_token || '',
              email: config.email || '',
              password: config.password || ''
            }
          };
          
          this.providerForm.patchValue(formData);
        }
        
        this.isPageLoading = false;
      },
      error: (err) => {
        console.error('Failed to load WhatsApp config', err);
        this.isPageLoading = false;
      }
    });
  }

  /* ---------- HELPERS ---------- */

  extractWhatsAppConfig(response: any): any | null {
    if (
      !response?.rows?.length ||
      !response.rows[0]?.whats_app_config
    ) {
      return null;
    }

    this.selected_id = response.rows[0].id;

    // whats_app_config is an object, not an array
    return response.rows[0].whats_app_config;
  }
}